import { CommandType } from '../types';

/**
 * AIレベル（6段階）
 */
export enum AILevel {
  /** ほぼランダム行動 */
  LV1 = 'LV1',
  /** 距離に応じた基本行動 */
  LV2 = 'LV2',
  /** Lv2 + HP・スタンス・リフレクター考慮 */
  LV3 = 'LV3',
  /** Lv3 + 行動パターン読み */
  LV4 = 'LV4',
  /** 常に最適行動（熟練） */
  LV5 = 'LV5',
  /** Lv5 + 多ターン先読み + 相手モデル化 + 混合戦略 + リソース最適化（達人） */
  LV6 = 'LV6',
}

/**
 * AIConfig のスキーマバージョン
 *
 * - v1: Lv1-5 用パラメータのみ（13個）
 * - v2: Lv6 パラメータ追加（lookAheadDepth, opponentModeling, mixedStrategy, resourceOptimization）
 *
 * LocalStorage 経由で古い形式を読み込む場合は migrateAIConfig() で v2 化される。
 */
export const AI_CONFIG_VERSION = 2;

/**
 * 種族別AI傾向（各コマンドの重み付け）
 */
export type SpeciesTendency = Record<CommandType, number>;

/**
 * プレイヤーが調整可能なAIパラメータ
 *
 * 各パラメータは0-100の整数値（thinkingLevelのみ1-5）。
 * 50がニュートラル（既存AIと同じ挙動）、0/100が極端な傾向。
 */
export interface AIConfig {
  /** 攻撃性 (0-100): 攻撃コマンド(WEAPON_ATTACK, SPECIAL_ATTACK)の重み。50=標準 */
  aggression: number;
  /** 距離志向 (0-100): 0=近距離志向(ADVANCE重視)、100=遠距離志向(RETREAT重視)。50=標準 */
  distancePreference: number;
  /** スタンス傾向 (0-100): 0=守勢寄り(STANCE_B/DEFENSIVE)、100=攻勢寄り(STANCE_A/OFFENSIVE)。50=標準 */
  stanceTendency: number;
  /** リフレクター使用率 (0-100): REFLECTOR使用頻度。50=標準 */
  reflectorUsage: number;
  /** 思考レベル (1-5): AIレベル選択 */
  thinkingLevel: AILevel;
  /** 特殊攻撃志向 (0-100): 0=武器攻撃寄り、100=特殊攻撃寄り。50=標準 */
  specialPreference: number;
  /** リスク許容度 (0-100): 0=HP低下時守備的、100=HP低下時でも攻撃的。50=標準。Lv3以上でのみ有効 */
  riskTolerance: number;
  /** パターン読み (0-100): 相手の行動パターンを読む度合い。11以上で有効、10以下でパターン分析を無効化。Lv4以上でのみ有効 */
  patternReading: number;
  /** カウンター傾向 (0-100): カウンター戦略の強さ。50=標準。Lv4以上でのみ有効 */
  counterTendency: number;
  /** 適応力 (0-100): 成功パターン学習の反映度。50=標準。Lv5でのみ有効 */
  adaptability: number;
  /** メタ読み (0-100): 裏をかく度合い。50=標準。Lv5でのみ有効 */
  metaReading: number;
  /** 予測不能性 (0-100): 行動のランダム性。フォールバック率（Lv3相当の行動を取る確率）を調整。0=フォールバックなし、50=標準、100=最大40%。Lv4以上でのみ有効 */
  unpredictability: number;
  /**
   * コンボ志向 (0-100): 1st/2nd連携重視度。50=標準
   * @未実装 現在バトルロジックに影響しない。将来実装予定
   */
  comboSynergy: number;
  /** 先読み深度 (1-5): Lv6でのみ有効。1=次ターンのみ、5=5ターン先まで。標準=3 */
  lookAheadDepth: number;
  /** 相手モデル化 (0-100): 相手アーキタイプのベイズ推定強度。50=標準。Lv6でのみ有効 */
  opponentModeling: number;
  /** 混合戦略 (0-100): ナッシュ均衡的な確率分布選択の強さ。50=標準。Lv6でのみ有効 */
  mixedStrategy: number;
  /** リソース最適化 (0-100): 特殊攻撃回数等の全試合最適配分。50=標準。Lv6でのみ有効 */
  resourceOptimization: number;
}

/** AIConfigのデフォルト値（全てニュートラル、Lv3） */
export const DEFAULT_AI_CONFIG: AIConfig = {
  aggression: 50,
  distancePreference: 50,
  stanceTendency: 50,
  reflectorUsage: 50,
  thinkingLevel: AILevel.LV3,
  specialPreference: 50,
  riskTolerance: 50,
  patternReading: 50,
  counterTendency: 50,
  adaptability: 50,
  metaReading: 50,
  unpredictability: 50,
  comboSynergy: 50,
  lookAheadDepth: 3,
  opponentModeling: 50,
  mixedStrategy: 50,
  resourceOptimization: 50,
};
